import React from 'react';
import {Form, InputGroup, OverlayTrigger, Tooltip} from '@themesberg/react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronDown, faGlobe} from '@fortawesome/free-solid-svg-icons';
import {FormattedMessage} from 'react-intl';
import LANGUAGES, {findLanguage} from '../services/languages';

// Bloc « Traductions » d'un libellé saisi par le chauffeur (nom d'option, de véhicule,
// libellés d'un champ personnalisé...).
//
// Le libellé principal est saisi dans la langue par défaut du module ; ce bloc,
// replié par défaut, liste les AUTRES langues du module avec un champ chacune.
// Un champ vide = le libellé principal est affiché dans cette langue (repli),
// ce que le placeholder rend visible sans phrase d'explication.
//
// Deux formes de valeur, selon `fields` :
//   - sans `fields` (un seul libellé) : value = {code: 'libellé'}, baseName = libellé principal
//   - avec `fields` = [{key, baseName}, ...] (plusieurs libellés par langue, ex. colonnes 1 et 2
//     d'un champ) : value = {code: {key: 'libellé', ...}}. Un sous-champ dont le baseName est
//     vide n'est pas proposé (il n'existe pas dans la langue principale non plus).
//
// Props :
//   value            objet {code: ...} (les langues non renseignées sont absentes)
//   onChange(value)  renvoie un nouvel objet (jamais muté)
//   defaultLanguage  code de la langue du libellé principal (exclue de la liste)
//   baseName         libellé principal (forme simple), affiché en placeholder de chaque langue
//   fields           sous-libellés (forme structurée), voir ci-dessus
//   defaultOpen      déplié au montage (ex : quand des traductions existent déjà) ; le parent doit changer la
//                    key du composant pour réinitialiser cet état (la modale hôte n'est pas démontée)
//   idPrefix         préfixe des id des champs (accessibilité)

const isBlank = (v) => (v || '').trim() === '';

// Une langue est « renseignée » dès qu'un de ses libellés l'est (forme simple ou structurée).
export const isTranslated = (entry) => {
  if (entry == null) return false;
  if (typeof entry === 'string') return !isBlank(entry);
  return Object.values(entry).some(v => !isBlank(v));
};

export const countTranslations = (translations) =>
  LANGUAGES.filter(l => isTranslated((translations || {})[l.code])).length;

// Texte d'une traduction pour un affichage compact (infobulle) : « a / b » en forme structurée.
const translationText = (entry) => {
  if (entry == null) return '';
  if (typeof entry === 'string') return entry;
  return Object.values(entry).filter(v => !isBlank(v)).join(' / ');
};

// Chip « 🇫🇷 Français » à côté du libellé principal, avec l'explication au survol.
export const LanguageChip = ({code, intl}) => {
  const base = findLanguage(code);
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip><FormattedMessage id="MAIN_NAME_LANGUAGE_TOOLTIP" values={{language: base.label}}/></Tooltip>}>
      <span className="cc-lang-chip" tabIndex={0}>
        <span aria-hidden="true">{base.flag}</span> {base.label}
      </span>
    </OverlayTrigger>
  );
};

// Nom + pastille « 🌐 n » dans une liste quand des traductions existent (le détail au survol).
export const NameWithTranslations = ({name, translations}) => {
  const filled = LANGUAGES.filter(l => isTranslated((translations || {})[l.code]));
  if (filled.length === 0) {
    return name;
  }
  return (
    <span className="cc-name-with-langs">
      {name}
      <OverlayTrigger placement="top" overlay={
        <Tooltip>
          {filled.map(l => <div key={l.code}>{l.flag} {translationText(translations[l.code])}</div>)}
        </Tooltip>
      }>
        <span className="cc-langs-pill" tabIndex={0}>
          <FontAwesomeIcon icon={faGlobe}/> {filled.length}
        </span>
      </OverlayTrigger>
    </span>
  );
};

export default ({value = {}, onChange, defaultLanguage = 'fr', baseName = '', fields, defaultOpen = false, idPrefix = 'translation'}) => {
  // État initial seulement : le parent change la key du composant à chaque ouverture de modale.
  const [open, setOpen] = React.useState(defaultOpen);

  const structured = Array.isArray(fields);
  // Sous-libellés proposés : le premier toujours (libellé obligatoire, même pas encore saisi), les autres
  // seulement s'ils existent dans la langue principale.
  const subFields = structured ? fields.filter((f, i) => i === 0 || !isBlank(f.baseName)) : [{key: null, baseName}];

  // Les autres langues que celle du libellé principal. La langue par défaut reste listée si elle porte
  // encore une traduction (langue par défaut changée dans Paramètres après coup) : elle serait servie au
  // client sans être visible ici, le chauffeur doit pouvoir la vider.
  const others = LANGUAGES.filter(l => l.code !== defaultLanguage || isTranslated(value[l.code]));
  const filled = others.filter(l => isTranslated(value[l.code])).length;
  const base = findLanguage(defaultLanguage);

  const getText = (code, key) => {
    const entry = value[code];
    if (entry == null) return '';
    return structured ? (entry[key] || '') : entry;
  };

  const handleChange = (code, key, text) => {
    const next = {...value};
    if (structured) {
      const entry = {...(next[code] || {}), [key]: text};
      if (text === '') {
        delete entry[key];
      }
      if (Object.keys(entry).length === 0) {
        delete next[code];
      } else {
        next[code] = entry;
      }
    } else if (text === '') {
      delete next[code];
    } else {
      next[code] = text;
    }
    onChange(next);
  };

  return (
    <div className={`cc-translations ${open ? 'is-open' : ''}`}>
      <button type="button" className="cc-translations-toggle" aria-expanded={open}
              aria-controls={`${idPrefix}-body`} onClick={() => setOpen(!open)}>
        <FontAwesomeIcon icon={faGlobe} className="cc-translations-icon"/>
        <span className="cc-translations-title"><FormattedMessage id="TRANSLATIONS"/></span>
        <span className={`cc-translations-count ${filled > 0 ? 'has-some' : ''}`}>
          <FormattedMessage id="TRANSLATIONS_COUNT" values={{filled, total: others.length}}/>
        </span>
        <FontAwesomeIcon icon={faChevronDown} className="cc-translations-chevron"/>
      </button>

      {open && (
        <div id={`${idPrefix}-body`} className="cc-translations-body">
          <p className="cc-translations-hint">
            <FormattedMessage id="TRANSLATIONS_HINT" values={{flag: base.flag, language: base.label}}/>
          </p>
          {others.map(l => (
            <InputGroup className={`cc-translations-row ${subFields.length > 1 ? 'is-multi' : ''}`} key={l.code}>
              <InputGroup.Text as="label" htmlFor={`${idPrefix}-${l.code}${subFields[0].key ? '-' + subFields[0].key : ''}`} title={l.label}>
                <span className="cc-translations-flag" aria-hidden="true">{l.flag}</span>
                <span className="cc-translations-code">{l.code.toUpperCase()}</span>
              </InputGroup.Text>
              {subFields.map(f => (
                <Form.Control key={f.key || 'single'} type="text" maxLength={255}
                              id={`${idPrefix}-${l.code}${f.key ? '-' + f.key : ''}`}
                              placeholder={f.baseName || l.label}
                              value={getText(l.code, f.key)}
                              onChange={(e) => handleChange(l.code, f.key, e.target.value)}/>
              ))}
            </InputGroup>
          ))}
        </div>
      )}
    </div>
  );
};
